export const urlRegex = /^https?:\/\/[^\s]+\.[^\s]+/;

/** Matches HTTP(S), www-prefixed, and bare-domain URLs embedded in text. */
export const urlInTextRegex =
  /((?:(?:https?):\/\/|www\.)[^\s<]+|(?<![@\w.-])(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:[/?#][^\s<]*)?)/gim;

export type HttpsUrl = `https://${string}`;
export type MailtoLink = `mailto:${string}`;
export type TelLink = `tel:${string}`;

/**
 * Returns base64-encoded data URI
 */
export function dataUri(content: Buffer, mimeType: string) {
  const base64 = content.toString("base64");
  return `data:${mimeType};base64,${base64}`;
}

const SAFE_NEXT_BASE = "https://redo.invalid";

/**
 * Returns a guaranteed same-origin, path-absolute redirect target derived from a
 * user-controlled `next` value, or `fallback` when `next` is absent or would
 * navigate off-origin.
 *
 * Validation is done by parsing, not pattern-matching: `next` is resolved with
 * the same URL semantics the browser uses to navigate, against a throwaway
 * origin. Anything that resolves off that origin is rejected — this covers
 * protocol-relative (`//host`), backslash-obfuscated (`/\host`), absolute
 * (`https://host`), `javascript:`, and whitespace/control-char variants. A
 * same-origin value is returned normalized as `pathname + search + hash`, so the
 * raw attacker-supplied string is never reflected back.
 *
 * Isomorphic — safe to call in the browser and on the server (relies only on the
 * global `URL`).
 */
export function safeNextPath(
  next: string | null | undefined,
  fallback?: string,
): string;
export function safeNextPath(
  next: string | null | undefined,
  fallback: null,
): string | null;
export function safeNextPath(
  next: string | null | undefined,
  fallback: string | null = "/",
): string | null {
  if (!next) {
    return fallback;
  }
  try {
    const url = new URL(next, SAFE_NEXT_BASE);
    if (url.origin !== SAFE_NEXT_BASE) {
      return fallback;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

export const parseUrl = (
  urlString: string,
  removeTrailingSlash = true,
  useHttpFallback = false,
): URL => {
  if (!/^https?:\/\//i.test(urlString)) {
    if (useHttpFallback) {
      urlString = `http://${urlString}`;
    } else {
      urlString = `https://${urlString}`;
    }
  }

  if (removeTrailingSlash && urlString.endsWith("/")) {
    urlString = urlString.slice(0, -1);
  }

  try {
    return new URL(urlString);
  } catch (error) {
    throw new Error(`Invalid URL: ${urlString}`);
  }
};

/** Normalize a raw URL to an https string, or "" when absent/unparseable. */
export function toHttpsUrl(raw: string | null | undefined): string {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return "";
  }
  try {
    return parseUrl(trimmed).toString();
  } catch {
    return "";
  }
}

const imageFileExtensionRegex = /\.(?:apng|avif|gif|jpe?g|png|webp)$/i;

/**
 * Returns whether a URL points directly to a browser-displayable image file.
 * Query parameters and fragments are intentionally excluded from the check.
 */
export function isImageUrl(raw: string | null | undefined): boolean {
  const normalizedUrl = toHttpsUrl(raw);
  if (!normalizedUrl) {
    return false;
  }

  try {
    return imageFileExtensionRegex.test(new URL(normalizedUrl).pathname);
  } catch {
    return false;
  }
}
